import React, { useMemo, useRef, useState } from 'react';
import Pagination from '@/components/pagination';
import '@/assets/styles/views/coach/students.scss';
import { useNavigate } from 'react-router';
import { NavigationPath } from '@/router/navigation-path.ts';
import { useApi } from '@/api/useApi.tsx';
import { useMutationProvider, useQueryProvider } from '@/hooks';
import CustomPlaceholder from '@/components/custom-placeholder';
import NoStudents from '@/assets/svg/no-students-placeholder.svg';
import { QUERY_KEYS } from '@/constants/QueryKeys.ts';
import { useSizes } from '@/utility/responsive.ts';
import { ManualCalendarSlotUpdateRequestLiveServiceTypeEnum } from '@dopingtech/live_expert_bff_api_ts/api.ts';
import { CallModal, type CallModalRef } from '@/components/call-modal';
import { Phone, RefreshCw } from 'lucide-react';
import { useDevicePermissions } from '@/context/device-permission-context.tsx';
import { useFeatureFlag } from 'configcat-react';
import { StudentsFilters } from '../../../components/students-filters/StudentsFilters.tsx';
import { components } from 'react-select';
import { Modal, RadioGroup, Select, TableLoader } from '@/components';
import { If } from 'vite-plugin-react-control-statements';
import { formatStudentDates } from '@/utility/formatters/studentInfoFormatter.ts';
import { useAuthStore } from '@/zustand/auth/store.ts';
import { jsonTryParse, parseJwt } from '@/utility';
import { sendMessageToWebView } from '@/utility/webview.ts';
import { PostMessage } from '@/constants/post-message.ts';

const CustomControl = ({ children, ...props }) => {
	const { onMenuToggle } = props.selectProps;
	return (
		<components.Control {...props}>
			<div
				className="button-sort"
				onClick={e => {
					e.stopPropagation();
					onMenuToggle();
				}}
				onMouseDown={e => e.stopPropagation()}>
				<i className="icons-sort-from-top-to-bottom" />
			</div>
		</components.Control>
	);
};

const sortOptions = [
	{ value: SortType.MATCHING_DATE, label: 'Atanma tarihine göre sırala (yeniden eskiye)' },
	{ value: SortType.LAST_MEETING, label: 'Son yapılan görüşme tarihine göre sırala' },
	{ value: SortType.MEETING_COUNT, label: 'Görüşme sayısına göre sırala' }
];

interface Student {
	id: string;
	shortName: string;
	name: string;
	grade: string;
	avatar: string;
	meetingCount: number;
	lastSolved: number | string;
	lastMeeting: string;
	isHighlighted: boolean | null;
	matchingDate: string;
	isTopRanked?: boolean | null;
	isRenewalStudent?: boolean | null;
}

enum SortType {
	MEETING_COUNT = 'totalInteractionCount',
	CANCELLED_MEETING = 'cancelledMeetingCount',
	LAST_MEETING = 'lastInteractionDate',
	MATCHING_DATE = 'matchingDate'
}

interface SortState {
	field: SortType | null;
	direction: 'asc' | 'desc';
}

const ITEMS_PER_PAGE = 10;

const Students: React.FC = () => {
	const { isMobile } = useSizes();
	const { data: authData } = useAuthStore();
	const [inputValue, setInputValue] = useState<string>('');
	const [searchQuery, setSearchQuery] = useState<string>('');
	const [currentPage, setCurrentPage] = useState<number>(1);
	const [allStudentsLength, setAllStudentsLength] = useState<number>(0);
	const [sortState, setSortState] = useState<SortState>({
		field: null,
		direction: 'asc'
	});

	const [studentsQuery, setStudentsQuery] = useState<any>();

	const [studentSortMenuIsOpen, setStudentSortMenuIsOpen] = useState(false);
	const [selectedStudentSortOption, setSelectedStudentSortOption] = useState(null);
	const [openMobileSortModal, setOpenMobileSortModal] = useState(false);

	const { value: isActiveCallStudentLecture } = useFeatureFlag('isactivecallstudentlecture', false);
	const { value: isActiveCallStudentLectureExpertIds } = useFeatureFlag('isActiveCallStudentLectureExpertIds', '[]');

	const { controlPermission } = useDevicePermissions();
	const { StudentStatisticControllerApi, StudentControllerApi } = useApi();
	const navigate = useNavigate();
	const searchRef = useRef<HTMLInputElement>(null);
	const callModalRef = useRef<CallModalRef>(null);
	const expertId = useMemo(() => (authData?.access_token ? parseJwt(authData?.access_token)?.additional_info?.expert_id : ''), [authData?.access_token]);
	console.log(isActiveCallStudentLectureExpertIds);
	const callStudentActiveWithId = useMemo(() => jsonTryParse(isActiveCallStudentLectureExpertIds).some(id => id === parseInt(expertId)), [expertId, isActiveCallStudentLectureExpertIds]);

	useQueryProvider(
		[QUERY_KEYS.findStudentDetailedStatisticsOfLoggedInExpert, ''],
		() =>
			StudentStatisticControllerApi.findStudentDetailedStatisticsOfLoggedInExpert(
				ManualCalendarSlotUpdateRequestLiveServiceTypeEnum.OneOnOneLecture,
				'',
				undefined,
				undefined,
				undefined,
				undefined,
				undefined,
				undefined,
				undefined,
				undefined,
				0,
				ITEMS_PER_PAGE,
				undefined
			),
		{
			enabled: true,
			onSuccess: data => {
				if (data?.data?.data?.totalElements) {
					setAllStudentsLength(data.data.data.totalElements);
				}
			}
		}
	);

	const {
		data: studentStatisticsResponse,
		isLoading,
		isFetching
	} = useQueryProvider(
		[QUERY_KEYS.findStudentDetailedStatisticsOfLoggedInExpert, searchQuery, JSON.stringify(studentsQuery), currentPage?.toString() || '', JSON.stringify(sortState)],
		() => {
			return StudentStatisticControllerApi.findStudentDetailedStatisticsOfLoggedInExpert(
				ManualCalendarSlotUpdateRequestLiveServiceTypeEnum.OneOnOneLecture,
				studentsQuery?.matchingDateFrom?.toISOString() ?? undefined,
				studentsQuery?.matchingDateTo?.toISOString() ?? undefined,
				studentsQuery?.lastInteractionDateFrom?.toISOString() ?? undefined,
				studentsQuery?.lastInteractionDateTo?.toISOString() ?? undefined,
				searchQuery,
				studentsQuery?.totalInteractionCount ? studentsQuery?.totalInteractionCount.split(',').map(i => Number(i)) : undefined,
				undefined,
				undefined,
				undefined,
				currentPage - 1,
				ITEMS_PER_PAGE,
				sortState.field ? [`${sortState.field},${sortState.direction}`] : undefined
			);
		},
		{ keepPreviousData: true }
	);

	const students: Student[] = useMemo(() => {
		if (!studentStatisticsResponse?.data?.data?.content) return [];

		return studentStatisticsResponse.data.data.content.map(stat => {
			const { formattedDate, isMoreThan21Days } = formatStudentDates(stat.lastInteractionDate);
			const { formattedDate: formattedMatchingDate } = formatStudentDates(stat.matchingDate);

			return {
				id: stat?.studentId?.toString() as string,
				shortName: `${stat.studentName?.charAt(0)?.toUpperCase() || ''} ${stat.studentSurname?.charAt(0)?.toUpperCase() || ''}`,
				name: `${stat.studentName || ''} ${stat.studentSurname || ''}`,
				grade: stat.preferredGradeName || '-',
				gradeId: stat.preferredGradeId || undefined,
				avatar: stat.avatarUrl || undefined,
				meetingCount: stat.totalInteractionCount || 0,
				lastSolved: stat.cancelledAppointmentCount || '-',
				lastMeeting: formattedDate,
				isHighlighted: isMoreThan21Days,
				matchingDate: formattedMatchingDate,
				isTopRanked: stat?.isTopRanked,
				isRenewalStudent: (stat as any)?.isRenewalStudent ?? null
			};
		});
	}, [studentStatisticsResponse]);

	const totalPages = useMemo(() => {
		return studentStatisticsResponse?.data?.data?.totalPages || 1;
	}, [studentStatisticsResponse]);

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
		setInputValue(e.target.value);
	};

	const handleSearch = (): void => {
		setSearchQuery(inputValue);
		setCurrentPage(1);
	};

	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
		if (e.key === 'Enter') {
			handleSearch();
		}
	};

	const handleClearSearch = (): void => {
		if (inputValue === '' && searchQuery !== '') {
			setSearchQuery('');
			setCurrentPage(1);
		}
	};

	React.useEffect(() => {
		if (inputValue === '') {
			handleClearSearch();
		}
	}, [inputValue]);

	const handleSort = (field: SortType): void => {
		if (sortState.field === field) {
			setSortState(prevState => ({
				...prevState,
				direction: prevState.direction === 'asc' ? 'desc' : 'asc'
			}));
			return;
		}

		if (sortState.field !== field) {
			setCurrentPage(1);
			setSortState({
				field,
				direction: 'asc'
			});
			return;
		}
	};

	const paginatedStudents = useMemo((): Student[] => {
		return students;
	}, [students]);

	const getSortIcon = (field: SortType): JSX.Element => {
		if (sortState.field === field) {
			return <i className={`icons-alt-arrow-${sortState.direction === 'asc' ? 'down' : 'up'}`} />;
		}
		return <i className="icons-alt-arrow-down half-transparent " />;
	};

	const handleMessageStudent =
		(studentId: string) =>
		(e: React.MouseEvent<HTMLButtonElement>): void => {
			e.stopPropagation();
			navigate(NavigationPath.messages, {
				state: { studentId }
			});
		};

	const handleStudentClick = (student: Student) => (): void => {
		navigate(NavigationPath.gotItStudentDetails, {
			state: { studentId: student.id }
		});
	};

	const handleCallStudent =
		(student: Student) =>
		(e: React.MouseEvent<HTMLButtonElement>): void => {
			e.stopPropagation();
			controlPermission(() => {
				const studentItem = {
					studentId: student.id,
					studentName: student.name
				};
				callModalRef.current?.handleCallStudent(studentItem);
			});
		};

	const { safeMutate: getPackageRenewalLink } = useMutationProvider((studentId: string) => StudentControllerApi.getPackageRenewalLink(Number(studentId)), {
		onSuccess: response => {
			const url = response?.data?.data;
			if (!url) return;
			const sent = sendMessageToWebView({ type: PostMessage.openExternalLink, data: { link: url } });
			if (!sent) {
				window.open(url, '_blank');
			}
		}
	});

	const handleRenewalLink =
		(student: Student) =>
		(e: React.MouseEvent<HTMLButtonElement>): void => {
			e.stopPropagation();
			getPackageRenewalLink(student.id);
		};

	return (
		<div className="students-container">
			<div className="page-header">
				<div className="left-side">
					<h1>Öğrencilerim</h1>
					{!isMobile && (
						<CustomPlaceholder
							ready={allStudentsLength !== 0 || !isLoading}
							width={235}
							height={19}
							rows={1}
							type={'rect'}>
							{allStudentsLength !== 0 && (
								<p>
									Toplam <strong>{studentStatisticsResponse?.data?.data?.totalElements || 0}</strong> öğrenciniz bulunmaktadır!
								</p>
							)}
						</CustomPlaceholder>
					)}
				</div>
				<CustomPlaceholder
					ready={allStudentsLength !== 0 || !isLoading}
					type="rect"
					rows={1}
					className="search-container"
					width={249}
					height={38}>
					{allStudentsLength !== 0 && (
						<div className="search-container">
							<div className="new-search-input">
								<input
									type="text"
									placeholder="Öğrenci ara"
									value={inputValue}
									onChange={handleInputChange}
									onKeyDown={handleKeyDown}
									ref={searchRef}
								/>
								<button
									onClick={handleSearch}
									className="search-button">
									<i className="icons-magnifier" /> Ara
								</button>
							</div>
						</div>
					)}
				</CustomPlaceholder>

				{isMobile && allStudentsLength !== 0 && !isLoading && (
					<p className="mobile-total-count">
						Toplam <strong>{studentStatisticsResponse?.data?.data?.totalElements || 0}</strong> öğrenciniz bulunmaktadır!
					</p>
				)}
			</div>

			<If condition={isMobile}>
				<div className="mobile-sort">
					<div
						className="button-sort"
						onClick={() => {
							setOpenMobileSortModal(true);
						}}>
						<i className="icons-sort-from-top-to-bottom" />
					</div>
				</div>
			</If>
			<div className="students-filters-area">
				<StudentsFilters updateStudentFilters={setStudentsQuery} />
			</div>

			{isLoading ? (
				<TableLoader />
			) : allStudentsLength !== 0 ? (
				<div className="students-table-wrap">
					{isFetching && <TableLoader variant="overlay" />}
					<div className="students-table">
						<div className="table-header">
							<div className="col col-students ">Id</div>
							<div className="col col-students ">Öğrenci</div>
							<div
								className="col sortable"
								onClick={() => handleSort(SortType.MATCHING_DATE)}>
								<span>Atanma Tarihi</span>
								{getSortIcon(SortType.MATCHING_DATE)}
							</div>
							<div
								className="col sortable"
								onClick={() => handleSort(SortType.MEETING_COUNT)}>
								<span>Görüşme Sayısı</span>
								{getSortIcon(SortType.MEETING_COUNT)}
							</div>
							<div
								className="col sortable"
								onClick={() => handleSort(SortType.CANCELLED_MEETING)}>
								<span>İptal Edilen</span>
								{getSortIcon(SortType.CANCELLED_MEETING)}
							</div>
							<div
								className="col sortable"
								onClick={() => handleSort(SortType.LAST_MEETING)}>
								<span>Son Görüşme</span>
								{getSortIcon(SortType.LAST_MEETING)}
							</div>
							<div className="col sort-group">
								<Select
									options={sortOptions}
									value={selectedStudentSortOption}
									onChange={option => {
										setSelectedStudentSortOption(option);
										handleSort(option.target.value);
									}}
									menuIsOpen={studentSortMenuIsOpen}
									onMenuOpen={() => setStudentSortMenuIsOpen(true)}
									onMenuClose={() => setStudentSortMenuIsOpen(false)}
									onMenuToggle={() => setStudentSortMenuIsOpen(prev => !prev)}
									components={{ Control: CustomControl }}
									isSearchable={false}
									isSortSelect
								/>
							</div>
						</div>

						<div className="table-body">
							{paginatedStudents.length === 0 ? (
								<div className="empty-state">Öğrenci bulunamadı</div>
							) : (
								paginatedStudents.map((student, index) => (
									<div
										key={index + student.id}
										className={`student-row`}
										onClick={handleStudentClick(student)}>
										<div className="col-student-id">
											<span>{student.id}</span>
										</div>
										<div className="col-student">
											<If condition={!!student?.isTopRanked}>
												<div className="col-student-top">
													<div className="student-top-ranked">
														<i className="icons-medal" />
														Derece Öğrencisi
													</div>
												</div>
											</If>
											<div className="col-student-bottom">
												{student?.avatar ? (
													<img
														src={student.avatar}
														alt={student.name}
													/>
												) : (
													<div className="avatar-placeholder">{student?.shortName}</div>
												)}

												<div className="student-info">
													<div className="name">{student.name}</div>
													<div className="grade">{student.grade}</div>
												</div>
											</div>
										</div>

										<div className="col-matching-date">{student.matchingDate}</div>

										<div className="col-meeting-count">{student.meetingCount}</div>

										<div
											className="col-cancelled"
											data-value={student.lastSolved}>
											{student.lastSolved}
										</div>

										<div className="col-last-meeting">
											<span className={student.isHighlighted ? 'highlight' : ''}>{student.lastMeeting}</span>
										</div>
										<div className="mobile-col-student-id">{student.id}</div>
										<div className="col-message">
											<button
												className="message-button"
												onClick={handleMessageStudent(student.id)}>
												<i className="icons-letter" />
												{isMobile ? 'Mesaj Gönder' : 'Mesaj'}
											</button>
											{(isActiveCallStudentLecture || callStudentActiveWithId) && (
												<button
													className="btn-call"
													onClick={handleCallStudent(student)}
													title="Öğrenciyi Ara">
													<Phone size={16} />
													Ara
												</button>
											)}
											{student?.isRenewalStudent && (
												<button
													className="btn-renewal"
													onClick={handleRenewalLink(student)}
													title="Yenileme Paketlerini Gör">
													<RefreshCw size={16} />
													{isMobile ? 'Yenileme Paketlerini Gör' : 'Yenile'}
												</button>
											)}
										</div>
									</div>
								))
							)}
						</div>
					</div>
				</div>
			) : (
				<div className="no-students">
					<img
						src={NoStudents}
						alt="No Students"
					/>
					<h2>Henüz size atanmış öğrenci bulunmamaktadır.</h2>
					<p>Öğrencileriniz size atandıkça bu alanda listelenmeye başlayacak!</p>
				</div>
			)}
			{totalPages > 1 && (
				<div className="pagination-container">
					<Pagination
						currentPage={currentPage}
						totalPages={totalPages}
						onPageChange={setCurrentPage}
					/>
				</div>
			)}
			<CallModal
				ref={callModalRef}
				liveServiceType={ManualCalendarSlotUpdateRequestLiveServiceTypeEnum.OneOnOneLecture}
			/>
			<Modal
				isOpen={openMobileSortModal}
				onClose={() => setOpenMobileSortModal(false)}
				contentClassName="mobile-select-bottom-sheet"
				showCloseButton
				closeOnClickOutside={true}
				variant="bottom"
				customXIcon={<i className="icons-x" />}>
				<h6 className="bottom-sheet-title">Sırala</h6>
				<RadioGroup
					direction="vertical"
					options={sortOptions}
					onChange={value => {
						setSelectedStudentSortOption(value);
						setOpenMobileSortModal(false);
						handleSort(value);
					}}
					name={'services-options'}
					value={selectedStudentSortOption}
				/>
			</Modal>
		</div>
	);
};

export default Students;
